import { useNumericInput } from "./useNumericInput";

export interface CurrencyInputProps {
  value: number;
  onChange?: (value: number) => void;
  placeholder?: string;
  errorMessage?: string;
  required?: boolean;
  emptyFallback?: number;
  align?: "left" | "right";
  disabled?: boolean;
  /** For computed, non-editable amounts (Total, Grand Total): renders a plain formatted value. */
  readOnly?: boolean;
  id?: string;
  className?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  "aria-label"?: string;
}

export const formatIDR = (n: number) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(n || 0);

const digitsOnly = (raw: string) => raw.replace(/\D/g, "");

/**
 * Rupiah-formatted input. Behaves like NumberInput (empty-while-editing,
 * never forces "0"), but renders/parses using Intl currency formatting.
 * Pass readOnly for computed fields like Total / Grand Total.
 */
export default function CurrencyInput({
  value,
  onChange,
  placeholder,
  errorMessage = "This field is required.",
  required = false,
  emptyFallback = 0,
  align = "right",
  disabled = false,
  readOnly = false,
  id,
  className = "",
  onKeyDown,
  ...aria
}: CurrencyInputProps) {
  // Hooks must run unconditionally on every render — readOnly only changes
  // what we return below, never whether this hook is called at all.
  const { displayValue, inputRef, handleChange, handleFocus, handleBlur, isInvalid } =
    useNumericInput({
      value,
      onChange: onChange ?? (() => {}),
      format: formatIDR,
      parseDigits: digitsOnly,
      required,
      emptyFallback,
    });

  if (readOnly) {
    return (
      <input
        id={id}
        type="text"
        value={formatIDR(value)}
        disabled
        readOnly
        className={[
          "w-full rounded-md border border-transparent bg-gray-50 px-2.5 py-2 text-sm font-medium tabular-nums text-gray-700 outline-none",
          align === "right" ? "text-right" : "text-left",
          className,
        ].join(" ")}
        {...aria}
      />
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        aria-invalid={isInvalid || undefined}
        className={[
          "w-full rounded-md border bg-transparent px-2.5 py-2 text-sm tabular-nums text-gray-800 outline-none transition-colors",
          "placeholder:text-gray-400 hover:border-gray-200 focus:bg-white focus:ring-2",
          align === "right" ? "text-right" : "text-left",
          isInvalid
            ? "border-red-300 focus:border-red-400 focus:ring-red-100"
            : "border-transparent focus:border-blue-400 focus:ring-blue-100",
          className,
        ].join(" ")}
        {...aria}
      />
      {isInvalid && (
        <p className="absolute left-0 top-full mt-0.5 whitespace-nowrap text-xs text-red-500">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
